<script setup>
import { defineAsyncComponent, onMounted, ref, watch } from 'vue'
import { getFullNameByUser, searchUsers } from '@/services/userService.js'

const SearchAttributeSelect = defineAsyncComponent(() =>
  import('@/components/Generics/Filters/SearchAttributeSelect.vue')
)

import TranslationProvider from '@/provider/translations.js'
import { useFiltersStore } from '@/services/store.js'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faFilter } from '@digitalpa/pro-solid-svg-icons'
import { attr_gec_category_code, attr_gec_category_description } from '@/services/compiledPollService.js'

library.add(faFilter)

const props = defineProps({
  isLoading: Boolean,
  pageType: {
    type: String,
    default: 'compiled-poll-list'
  },
  currentFilters: {
    default: '',
    type: String
  }
})

const filtersStore = useFiltersStore()

const userListRE = ref([])
const userListREA = ref([])
const userRE = ref(null)
const userREA = ref(null)
const currentReaSearch = ref('')
const currentReSearch = ref('')

onMounted(async () => {
  await setFilters()
})

const setFilters = async () => {
  const filters = filtersStore.filters[props.pageType]

  if (filters['gec_involved_subject_rating_evaluator_user']) {
    userListRE.value = [filters['gec_involved_subject_rating_evaluator_user']]
    userRE.value = filters['gec_involved_subject_rating_evaluator_user']
  }
  if (filters['gec_involved_subject_rating_evaluation_approver_user']) {
    userListREA.value = [filters['gec_involved_subject_rating_evaluation_approver_user']]
    userREA.value = filters['gec_involved_subject_rating_evaluation_approver_user']
  }
}

const resetFilters = () => {
  userRE.value = null
  userREA.value = null
}

async function fetchUsers(fullSearch, mainContractor) {
  if (fullSearch.length < 3) {
    return
  }

  try {
    const response = await searchUsers('default-vr', {
      fullSearch: fullSearch,
      page: 1,
      limit: 20,
      excludeData: 0
    })

    if (mainContractor) {
      userListRE.value = response.data
    } else {
      userListREA.value = response.data
    }
  } catch (error) {
  }
}

watch(userRE, (newVal) => {
  if (!newVal) {
    filtersStore.filters[props.pageType].gec_involved_subject_rating_evaluator = null
    filtersStore.filters[props.pageType].gec_involved_subject_rating_evaluator_user = null
  }
})

watch(userREA, (newVal) => {
  if (!newVal) {
    filtersStore.filters[props.pageType].gec_involved_subject_rating_evaluation_approver = null
    filtersStore.filters[props.pageType].gec_involved_subject_rating_evaluation_approver_user = null
  }
})

defineExpose({
  resetFilters
})
</script>

<template>

  <div class="col-12 col-lg-3 px-2">
    <div class="mb-2">
      <SearchAttributeSelect
        :label="TranslationProvider.getTrans('app.codice_categoria')"
        :attribute="attr_gec_category_code"
        :page-type="props.pageType"
      ></SearchAttributeSelect>
    </div>
  </div>

  <div class="col-12 col-lg-3 px-2">
    <div class="mb-2">
      <SearchAttributeSelect
        :label="TranslationProvider.getTrans('app.descrizione_categoria')"
        :attribute="attr_gec_category_description"
        :page-type="props.pageType"
      ></SearchAttributeSelect>
    </div>
  </div>

  <div class="col-12 col-lg-3 px-2">
    <div class="mb-2">
      <label for="rating_evaluation_approver" class="form-label">
        {{ TranslationProvider.getTrans('app.rating_evaluation_approver') }}
      </label>

      <v-select
        id="rating_evaluation_approver"
        @search="(searchQuery, loading) => {
                  currentReaSearch = searchQuery;
                  loading(true);
                  fetchUsers(searchQuery, false).then(() => {
                    loading(false);
                  });
                }"
        v-model="userREA"
        :options="userListREA"
        label="username"
        :filterable="false"
        @search:blur="() => {userListREA = []}"
        @search:selected="() => {userListREA = []}"
        :placeholder="TranslationProvider.getTrans('app.rating_evaluation_approver')"
        @option:selecting="(selectedOption) => {
                filtersStore.filters[props.pageType].gec_involved_subject_rating_evaluation_approver_user = selectedOption;
                filtersStore.filters[props.pageType].gec_involved_subject_rating_evaluation_approver = selectedOption.reference;
              }"
        clearable
      >
        <template v-slot:option="option">
          {{
            getFullNameByUser(option)
          }}
        </template>
        <template v-slot:selected-option="option">
          {{
            getFullNameByUser(option)
          }}
        </template>
        <template v-slot:no-options>
              <span v-if="currentReaSearch.length >= 3">
                  {{ TranslationProvider.getTrans('app.no_result_found') }}
              </span>
          <span v-else>
                 {{ TranslationProvider.getTrans('app.digita_per_cercare') }}
              </span>
        </template>
      </v-select>

      <small class="form-text">
        {{ TranslationProvider.getTrans('app.select2_min_chars') }}
      </small>
    </div>
  </div>

  <div class="col-12 col-lg-3 px-2">
    <div class="mb-2">
      <label for="rating_evaluator" class="form-label">
        {{ TranslationProvider.getTrans('app.rating_evaluator') }}
      </label>
      <v-select
        id="rating_evaluator"
        @search="(searchQuery, loading) => {
                currentReSearch = searchQuery
                loading(true);
                fetchUsers(searchQuery, true).then(() => {
                  loading(false);
                });
              }"
        v-model="userRE"
        :options="userListRE"
        label="username"
        @search:blur="() => {userListRE = []}"
        @search:selected="() => {userListRE = []}"
        :filterable="false"
        :placeholder="TranslationProvider.getTrans('app.rating_evaluator')"
        clearable
        @option:selecting="(selectedOption) => {
              filtersStore.filters[props.pageType].gec_involved_subject_rating_evaluator_user = selectedOption
              filtersStore.filters[props.pageType].gec_involved_subject_rating_evaluator = selectedOption.reference
            }"
      >
        <template v-slot:option="option">
          {{
            getFullNameByUser(option)
          }}
        </template>
        <template v-slot:selected-option="option">
          {{
            getFullNameByUser(option)
          }}
        </template>
        <template v-slot:no-options>
              <span v-if="currentReSearch.length >= 3">
                {{ TranslationProvider.getTrans('app.no_result_found') }}
              </span>
          <span v-else>
                 {{ TranslationProvider.getTrans('app.digita_per_cercare') }}
              </span>
        </template>

      </v-select>
      <small class="form-text">
        {{ TranslationProvider.getTrans('app.select2_min_chars') }}
      </small>
    </div>
  </div>
</template>

<style scoped></style>
